import type { LeagueDynamicFieldValue } from "@/models/dynamicFieldValue";
import { dynamicValueIsEmpty } from "@/models/dynamicFieldValue";
import type { LeagueAthleteFieldDefinition } from "@/models/fieldDefinition";
import type { LeagueFieldSchema } from "@/models/schema";
import { athleteValueAllValues, athleteValueID, sortAthleteValues, type LeagueAthleteValue } from "@/models/schema";
import { scopedMapsByDerivingAthleteUniversalFields, type ScopedDynamicValueMaps } from "@/models/athleteUniversalFields";

export type AthleteValueFieldErrors = Record<string, string>;

export const EMPTY_SCOPED_VALUE_MAPS: ScopedDynamicValueMaps = {
  publicValues: {},
  organizationValues: {},
  leagueValues: {},
};

export function emptyAthleteSchema(leagueID: string, sportID?: string): LeagueFieldSchema {
  return {
    leagueID,
    sportID,
    version: 0,
    fields: [],
  };
}

export function visibleAthleteValues(values: LeagueAthleteValue[]): LeagueAthleteValue[] {
  return values.filter((value) => value.status !== "archived").sort(sortAthleteValues);
}

export function mapsForAthleteValue(value: LeagueAthleteValue, fields: LeagueAthleteFieldDefinition[]): ScopedDynamicValueMaps {
  return scopedMapsByDerivingAthleteUniversalFields(scopedMapsFromValues(athleteValueAllValues(value), fields), fields);
}

export function buildAthleteValue({
  leagueID,
  fields,
  existingValue,
  explicitName,
  maps,
  status,
  userID,
  now,
}: {
  leagueID: string;
  fields: LeagueAthleteFieldDefinition[];
  existingValue?: LeagueAthleteValue;
  explicitName?: string;
  maps: ScopedDynamicValueMaps;
  status: "draft" | "active";
  userID: string;
  now: Date;
}): LeagueAthleteValue {
  const routedMaps = scopedMapsByDerivingAthleteUniversalFields(maps, fields, [], now);
  return {
    id: existingValue?.id ?? athleteValueID(leagueID),
    leagueID,
    name: resolvedAthleteName(explicitName, routedMaps, fields),
    status,
    athleteUserID: trimmedNonEmpty(existingValue?.athleteUserID),
    organizationID: trimmedNonEmpty(existingValue?.organizationID),
    publicValues: routedMaps.publicValues,
    organizationValues: routedMaps.organizationValues,
    leagueValues: routedMaps.leagueValues,
    createdByUserID: existingValue?.createdByUserID ?? userID,
    createdAt: existingValue?.createdAt ?? now,
    updatedAt: now,
    updatedByUserID: userID,
  };
}

export function archivedAthleteValue(value: LeagueAthleteValue, userID: string, now: Date): LeagueAthleteValue {
  return {
    ...value,
    status: "archived",
    updatedAt: now,
    updatedByUserID: userID,
  };
}

export function validateAthleteMaps(
  fields: LeagueAthleteFieldDefinition[],
  maps: ScopedDynamicValueMaps,
  requiresRequiredFields: boolean,
): AthleteValueFieldErrors {
  const values = mergedMaps(maps);
  const errors: AthleteValueFieldErrors = {};
  for (const field of fields) {
    const value = values[field.id];
    if (!value || dynamicValueIsEmpty(value)) {
      if (requiresRequiredFields && field.isRequired) {
        errors[field.id] = `${field.label} is required.`;
      }
      continue;
    }
    const error = validateValue(field, value);
    if (error) errors[field.id] = error;
  }
  return errors;
}

export function resolvedAthleteName(
  explicitName: string | undefined,
  maps: ScopedDynamicValueMaps,
  fields: LeagueAthleteFieldDefinition[],
): string {
  const trimmed = trimmedNonEmpty(explicitName);
  if (trimmed) return trimmed;

  const values = mergedMaps(maps);
  const fieldIDByRole = new Map<string, string>();
  for (const field of fields) {
    if (field.universalAthleteRole && !fieldIDByRole.has(field.universalAthleteRole)) {
      fieldIDByRole.set(field.universalAthleteRole, field.id);
    }
  }
  const fullName = stringValue(values[fieldIDByRole.get("full-name") ?? ""]);
  if (fullName) return fullName;

  const firstName = stringValue(values[fieldIDByRole.get("first-name") ?? ""]);
  const lastName = stringValue(values[fieldIDByRole.get("last-name") ?? ""]);
  const joined = [firstName, lastName].filter(Boolean).join(" ").trim();
  return joined || "Athlete";
}

function scopedMapsFromValues(
  values: Record<string, LeagueDynamicFieldValue>,
  fields: LeagueAthleteFieldDefinition[],
): ScopedDynamicValueMaps {
  const maps: ScopedDynamicValueMaps = { publicValues: {}, organizationValues: {}, leagueValues: {} };
  for (const field of fields) {
    const value = values[field.id];
    if (!value || dynamicValueIsEmpty(value)) continue;
    const scopeKey =
      field.privacyScope === "public" ? "publicValues" : field.privacyScope === "organization" ? "organizationValues" : "leagueValues";
    maps[scopeKey][field.id] = value;
  }
  return maps;
}

function mergedMaps(maps: ScopedDynamicValueMaps): Record<string, LeagueDynamicFieldValue> {
  return { ...maps.leagueValues, ...maps.organizationValues, ...maps.publicValues };
}

function validateValue(field: LeagueAthleteFieldDefinition, value: LeagueDynamicFieldValue): string | null {
  switch (field.type) {
    case "text":
    case "phone":
      return validateStringBounds(field, value);
    case "email": {
      const base = validateStringBounds(field, value);
      if (base) return base;
      const text = stringValue(value);
      return text && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text) ? null : `Check ${field.label}.`;
    }
    case "url":
    case "photo": {
      const base = validateStringBounds(field, value);
      if (base) return base;
      const text = stringValue(value);
      if (!text) return `Check ${field.label}.`;
      try {
        const url = new URL(text);
        return url.protocol === "http:" || url.protocol === "https:" ? null : `Check ${field.label}.`;
      } catch {
        return `Check ${field.label}.`;
      }
    }
    case "integer":
      return value.kind === "int" ? validateNumberBounds(field, value.value) : `Check ${field.label}.`;
    case "decimal":
      return value.kind === "int" || value.kind === "double" ? validateNumberBounds(field, value.value) : `Check ${field.label}.`;
    case "boolean":
      return value.kind === "bool" ? null : `Check ${field.label}.`;
    case "date":
    case "dateTime":
      return value.kind === "date" && !Number.isNaN(value.value.getTime()) ? null : `Check ${field.label}.`;
    case "singleSelect":
      return value.kind === "string" && field.options.some((option) => option.id === value.value) ? null : `Check ${field.label}.`;
    case "multiSelect": {
      if (value.kind !== "stringList") return `Check ${field.label}.`;
      const allowed = new Set(field.options.map((option) => option.id));
      if (!value.value.every((entry) => allowed.has(entry))) return `Check ${field.label}.`;
      if (field.validation.minSelections !== undefined && value.value.length < field.validation.minSelections) return `Check ${field.label}.`;
      if (field.validation.maxSelections !== undefined && value.value.length > field.validation.maxSelections) return `Check ${field.label}.`;
      return null;
    }
  }
}

function validateStringBounds(field: LeagueAthleteFieldDefinition, value: LeagueDynamicFieldValue): string | null {
  const text = stringValue(value);
  if (text === undefined) return `Check ${field.label}.`;
  if (field.validation.minLength !== undefined && text.length < field.validation.minLength) return `Check ${field.label}.`;
  if (field.validation.maxLength !== undefined && text.length > field.validation.maxLength) return `Check ${field.label}.`;
  return null;
}

function validateNumberBounds(field: LeagueAthleteFieldDefinition, value: number): string | null {
  if (field.validation.minNumber !== undefined && value < field.validation.minNumber) return `Check ${field.label}.`;
  if (field.validation.maxNumber !== undefined && value > field.validation.maxNumber) return `Check ${field.label}.`;
  return null;
}

function stringValue(value: LeagueDynamicFieldValue | undefined): string | undefined {
  return value?.kind === "string" ? trimmedNonEmpty(value.value) : undefined;
}

function trimmedNonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}
